import { type AiResponse, CreateAiReportReq, type File, type Memo } from '@kalos/kalos-rpc';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  LoadingIcon,
  Textarea,
  toast,
} from '@kalos/ui';
import { CopyIcon, DownloadIcon, EyeOpenIcon, ImageIcon } from '@radix-ui/react-icons';
import { format, parseISO } from 'date-fns/esm';
import { ChevronLeft } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { ListRow } from '../../../../components/ListRow';
import { useAuth } from '../../../../context/AuthContext';
import { useMemoReportQuery } from '../../../../hooks/react-query/useMemoQuery';
import { useMobileDevice } from '../../../../hooks/useMedia';
import { PhotoThumbnail, PhotoViewer } from '../../ServiceCall/components/Notes/PhotoGallery';
import { copyReportToClipboard } from '../reportClipboard';
import { buildAiReportRequest } from './aiReportRequest';
import { ReportPhotoGalleryDialog } from './ReportPhotoGalleryDialog';
import { downloadReportText, useReportPhotos } from './useReportPhotos';

interface MemoReportFormProps {
  memoRequest: Memo;
  aiResponse?: AiResponse; // Optional: if provided, shows the report and uses memoIds from it
  enabled?: boolean;
  onClose?: () => void;
  /** Phones: Build PDF and Download PDF for a saved report, shown under Copy report. */
  onBuildPdf?: () => void;
  onDownloadPdf?: () => void;
  /** The label of the button that calls `onClose` after a report is shown. */
  closeLabel?: string;
  onSuccess?: () => void;
}

type ReportType = 'department' | 'status' | 'job' | 'custom';

type WorkflowStep = 'form' | 'generating' | 'generated';

interface ReportFormValues {
  reportType: ReportType;
  customQuery: string;
}

export const MemoReportForm = ({
  memoRequest,
  aiResponse,
  enabled = true,
  onClose,
  closeLabel = 'Close',
  onBuildPdf,
  onDownloadPdf,
  onSuccess,
}: MemoReportFormProps) => {
  const { user } = useAuth();
  // If aiResponse is provided, start in 'generated' state to show the report
  const [workflowStep, setWorkflowStep] = useState<WorkflowStep>(aiResponse ? 'generated' : 'form');
  const [isPhotoGridOpen, setIsPhotoGridOpen] = useState(false);
  const [openPhotoIndex, setOpenPhotoIndex] = useState<number | null>(null);
  const isMobileDevice = useMobileDevice();
  const [reportRequest, setReportRequest] = useState<CreateAiReportReq | null>(null);
  const form = useForm<ReportFormValues>({
    defaultValues: {
      reportType: 'job',
      customQuery: '',
    },
  });

  const reportType = form.watch('reportType');

  const reportTypes = [
    {
      id: 'department' as const,
      title: 'Department Summary',
      description: 'Breakdown by department with completion rates.',
      comingSoon: true,
    },
    {
      id: 'status' as const,
      title: 'Status Summary',
      description: 'Analyze notes by status (Pending, In Progress, Completed).',
      comingSoon: true,
    },
    {
      id: 'job' as const,
      title: 'Job Summary',
      description: 'Breakdown by job number with activity details.',
      comingSoon: false,
    },
    {
      id: 'custom' as const,
      title: 'Custom Query',
      description: 'Ask a specific question about your notes.',
      comingSoon: true,
    },
  ];

  const availableReportTypes = reportTypes.filter((type) => !type.comingSoon);

  const handleReportTypeClick = (type: ReportType) => {
    const reportTypeConfig = reportTypes.find((rt) => rt.id === type);
    if (!reportTypeConfig?.comingSoon) {
      form.setValue('reportType', type);
    }
  };

  // Check if we're in "view" mode (aiResponse provided) vs "generate" mode (regular filters)
  const isViewMode = useMemo(() => {
    return !!aiResponse; // If aiResponse is provided, we're viewing an existing report
  }, [aiResponse]);

  const {
    hasFilterApplied,
    hasMemoIds,
    memoQuery,
    memoClassificationQuery,
    allPhotoFiles,
    showPhotoGallery,
    setShowPhotoGallery,
    photoFiles,
    fileToMemoMap,
    handleViewAllPhotos,
    downloadZipMutation,
    handleDownloadPhotos,
  } = useReportPhotos({ memoRequest, aiResponse, enabled });

  const memoReportQuery = useMemoReportQuery({
    req: reportRequest || CreateAiReportReq.create(),
    enabled: !!reportRequest && workflowStep === 'generating', // Only fetch when we have a request and are generating
  });

  // Handle report query completion
  useEffect(() => {
    if (memoReportQuery.isSuccess && reportRequest && workflowStep === 'generating') {
      const shouldRunInBackground = reportRequest.background;

      if (shouldRunInBackground) {
        // Background processing - show message to check reports page
        toast({
          title: 'Report Generation Started',
          description:
            'Your report is being processed in the background. Check the Reports tab for updates.',
          variant: 'default',
        });
      } else {
        // Immediate response - show success and move to generated screen
        toast({
          title: 'Report Generated Successfully',
          description: 'Your report has been generated and is ready to view.',
          variant: 'success',
        });
      }

      setWorkflowStep('generated');
      onSuccess?.();
    } else if (memoReportQuery.isError && workflowStep === 'generating') {
      // Check if error is due to missing content/input
      const errorMessage = memoReportQuery.error?.toString() || '';
      const isMissingContentError =
        errorMessage.includes('prompt_variable_missing') ||
        errorMessage.includes('Missing prompt variables: input');

      toast({
        title: 'Report Generation Failed',
        description: isMissingContentError
          ? 'Reports are only generated when notes include audio or written content. Please select notes with content to generate a report'
          : 'There was an error generating your report. Please try again.',
        variant: 'destructive',
      });
      setWorkflowStep('form');
    }
  }, [
    memoReportQuery.isSuccess,
    memoReportQuery.isError,
    reportRequest,
    workflowStep,
    onSuccess,
    memoReportQuery.error,
  ]);

  const handleSubmit = async (values: ReportFormValues) => {
    setWorkflowStep('generating');
    try {
      // Use already loaded memos (data is fetched when dialog opens)
      const memos = memoQuery.data?.results || [];

      const built = buildAiReportRequest({
        memos,
        memoRequest,
        customQuery: values.customQuery,
        userId: user.id,
      });

      // Validate memo count - prevent generation if more than 100
      if (built.tooLarge) {
        setWorkflowStep('form');
        toast({
          title: 'Report Too Large',
          description:
            'This report is too large to generate in one go. Please narrow your filters (smaller date range or fewer notes) and try again.',
          variant: 'destructive',
        });
        return;
      }
      const req = built.req;

      // Update request state - this will enable the query and trigger it automatically
      setReportRequest(req);

      // The useEffect will handle the success/error states and toast notifications
    } catch (error) {
      console.error('Error generating report:', error);
      setWorkflowStep('form');
      toast({
        title: 'Report Generation Failed',
        description: 'There was an error generating your report. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleGenerateNew = () => {
    setWorkflowStep('form');
    form.reset();
  };

  // Generating state - only show when actually generating
  if (workflowStep === 'generating') {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingIcon className="h-12 w-12" />
          <p className="text-muted-foreground text-sm">Generating report...</p>
        </div>
      </div>
    );
  }

  const reportText = memoReportQuery.data?.textReport || aiResponse?.response || '';
  const reportTitle = reportTypes.find((rt) => rt.id === reportType)?.title;
  const copyReport = async () => {
    try {
      await copyReportToClipboard(reportText);
      toast({ title: 'Report copied', description: 'Paste it into an email or a message.' });
    } catch {
      toast({
        title: 'Couldn’t copy the report',
        description: 'Select the text and copy it instead.',
        variant: 'destructive',
      });
    }
  };
  const photoCaption = (photo: File) => {
    const memo = allPhotoFiles.fileToMemoMap.get(photo.id ?? 0);
    if (!memo) return null;
    return [
      memo.createdByName,
      memo.createdDate ? format(parseISO(memo.createdDate), 'MMM d, yyyy h:mm a') : '',
      memo.eventId ? `Job #${memo.eventId}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
  };

  // Generated screen on phones: one primary action (Copy report), the photos as one row that opens
  // a full-screen grid, and the report as text on the page.
  if (workflowStep === 'generated' && isMobileDevice) {
    const photoCount = allPhotoFiles.files.length;
    const secondaryActions = [
      !isViewMode && (
        <Button key="new" variant="outline" className="touch:min-h-11" onClick={handleGenerateNew}>
          New report
        </Button>
      ),
      onBuildPdf && (
        <Button key="pdf" variant="outline" className="touch:min-h-11" onClick={onBuildPdf}>
          Build PDF
        </Button>
      ),
      onDownloadPdf && (
        <Button key="download" variant="outline" className="touch:min-h-11" onClick={onDownloadPdf}>
          Download PDF
        </Button>
      ),
    ].filter(Boolean);
    return (
      <div className="flex flex-col gap-4">
        {onClose && (
          <Button variant="ghost" className="-ml-2 w-fit gap-2" onClick={onClose}>
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {closeLabel}
          </Button>
        )}
        {reportTitle && <h3 className="text-base font-semibold">{reportTitle}</h3>}
        {reportRequest?.background ? (
          <p className="text-muted-foreground text-sm">
            Your report is being written. Find it in My Reports once it&apos;s ready.
          </p>
        ) : (
          <Button className="w-full" disabled={!reportText} onClick={() => void copyReport()}>
            Copy report
          </Button>
        )}
        {secondaryActions.length > 0 && (
          <div className={`grid gap-2 ${secondaryActions.length > 1 ? 'grid-cols-2' : ''}`}>
            {secondaryActions}
          </div>
        )}
        {photoCount > 0 && (
          <ul aria-label="Photos" className="border-border -mx-4 border-y">
            <ListRow
              title={`Photos · ${photoCount}`}
              details={`From ${allPhotoFiles.memoCount} note${allPhotoFiles.memoCount === 1 ? '' : 's'}`}
              actionLabel="View"
              onSelect={() => setIsPhotoGridOpen(true)}
            />
          </ul>
        )}
        {!reportRequest?.background &&
          (reportText ? (
            <p className="whitespace-pre-wrap break-words text-sm">{reportText}</p>
          ) : (
            <p className="text-muted-foreground text-sm">Loading report…</p>
          ))}

        {isPhotoGridOpen && (
          <Dialog open onOpenChange={(open) => !open && setIsPhotoGridOpen(false)}>
            <DialogContent
              showClose={false}
              // Full screen: nothing to type, so it must not re-center itself for the keyboard.
              keepFocusVisibleAboveKeyboard={false}
              className="bg-background left-0 top-0 flex h-dvh w-full max-w-none translate-x-0 translate-y-0 flex-col gap-3 overflow-y-auto rounded-none border-0 p-4 sm:rounded-none"
            >
              <div className="flex items-center justify-between gap-2">
                <DialogTitle className="text-lg">Photos · {photoCount}</DialogTitle>
                <Button variant="ghost" onClick={() => setIsPhotoGridOpen(false)}>
                  Close
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 pb-[calc(env(safe-area-inset-bottom)+1rem)]">
                {allPhotoFiles.files.map((photo, index) => (
                  <PhotoThumbnail
                    key={photo.id}
                    file={photo}
                    onOpen={() => setOpenPhotoIndex(index)}
                  />
                ))}
              </div>
            </DialogContent>
          </Dialog>
        )}
        {openPhotoIndex !== null && (
          <PhotoViewer
            photos={allPhotoFiles.files}
            index={openPhotoIndex}
            onIndexChange={setOpenPhotoIndex}
            onClose={() => setOpenPhotoIndex(null)}
            caption={photoCaption}
          />
        )}
      </div>
    );
  }

  // Generated screen
  if (workflowStep === 'generated') {
    return (
      <>
        <div className="space-y-6">
          {/* Top Actions */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {reportTypes.find((rt) => rt.id === reportType) && (
                <Button variant="outline" size="sm" disabled>
                  {reportTypes.find((rt) => rt.id === reportType)?.title}
                </Button>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Only show "Generate New Report" if not in view mode (no idList) */}
              {!isViewMode && (
                <Button variant="outline" size="sm" onClick={handleGenerateNew}>
                  Generate New Report
                </Button>
              )}
              {onClose && (
                <Button variant="outline" size="sm" onClick={onClose}>
                  {closeLabel}
                </Button>
              )}
            </div>
          </div>

          {/* Photo Documentation Card - Show if we have photos from the memo query */}
          {allPhotoFiles.files.length > 0 && (
            <Card className="border-border border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-accent flex h-10 w-10 items-center justify-center rounded-lg">
                      <ImageIcon className="text-foreground h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="text-foreground font-semibold">Photo Documentation</h3>
                      <p className="text-muted-foreground text-sm">
                        {allPhotoFiles.files.length} photo
                        {allPhotoFiles.files.length === 1 ? '' : 's'} from {allPhotoFiles.memoCount}{' '}
                        note{allPhotoFiles.memoCount === 1 ? '' : 's'}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleViewAllPhotos}
                      className="gap-2"
                    >
                      <EyeOpenIcon className="h-4 w-4" />
                      View All Photos
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleDownloadPhotos}
                      disabled={downloadZipMutation.isPending || allPhotoFiles.files.length === 0}
                      isLoading={downloadZipMutation.isPending}
                      className="gap-2"
                    >
                      <DownloadIcon className="h-4 w-4" />
                      Download All Photos
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Report Section */}
          {reportRequest?.background ? (
            <div className="border-border bg-muted rounded-lg border p-8 text-center">
              <p className="text-foreground text-sm">
                Please check the My Reports Tab for your report once it&apos;s ready.
              </p>
            </div>
          ) : (
            <Card className="border-border border">
              <CardContent className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <h3 className="text-lg font-semibold">Report</h3>
                  {(memoReportQuery.data?.textReport || aiResponse?.response) && (
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void copyReport()}
                        className="gap-2"
                      >
                        <CopyIcon className="h-4 w-4" aria-hidden />
                        Copy
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          downloadReportText(
                            memoReportQuery.data?.textReport || aiResponse?.response || '',
                            aiResponse?.id,
                          )
                        }
                        className="gap-2"
                      >
                        <DownloadIcon className="h-4 w-4" />
                        Download as .txt
                      </Button>
                    </div>
                  )}
                </div>
                {memoReportQuery.data?.textReport || aiResponse?.response ? (
                  <div className="border-border bg-muted rounded-lg border p-4">
                    <pre className="text-foreground whitespace-pre-wrap font-sans text-sm">
                      {memoReportQuery.data?.textReport || aiResponse?.response}
                    </pre>
                  </div>
                ) : (
                  <div className="p-8 text-center">
                    <p className="text-muted-foreground text-sm">Loading report...</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        {/* Photo Gallery Dialog */}
        <ReportPhotoGalleryDialog
          open={showPhotoGallery}
          onOpenChange={setShowPhotoGallery}
          photoFiles={photoFiles}
          fileToMemoMap={fileToMemoMap}
          categories={memoClassificationQuery.data?.results}
        />
      </>
    );
  }

  // Form screen - Don't show if in view mode (idList provided)
  // In view mode, we should already be in 'generated' state, but add safety check
  if (isViewMode) {
    // If we're in view mode but somehow on form screen, go to generated
    // This shouldn't happen since we start in 'generated' when aiResponse is provided
    return null;
  }

  // Show loading state while fetching data
  if (memoQuery.isFetching && !memoQuery.data) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <LoadingIcon className="h-12 w-12" />
          <p className="text-muted-foreground text-sm">Loading notes...</p>
        </div>
      </div>
    );
  }

  // Show error state if fetch failed
  if (memoQuery.isError) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <p className="text-destructive text-sm">Error loading notes. Please try again.</p>
          {onClose && (
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
        {/* Photo Documentation Card - Show in form screen (only if we have memoIds) */}
        {hasMemoIds && allPhotoFiles.files.length > 0 && (
          <Card className="border-border border">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-accent flex h-10 w-10 items-center justify-center rounded-lg">
                    <ImageIcon className="text-foreground h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="text-foreground font-semibold">Photo Documentation</h3>
                    <p className="text-muted-foreground text-sm">
                      {allPhotoFiles.files.length} photo
                      {allPhotoFiles.files.length === 1 ? '' : 's'} from {allPhotoFiles.memoCount}{' '}
                      note{allPhotoFiles.memoCount === 1 ? '' : 's'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleViewAllPhotos}
                    className="gap-2"
                  >
                    <EyeOpenIcon className="h-4 w-4" />
                    View All Photos
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleDownloadPhotos}
                    disabled={downloadZipMutation.isPending || allPhotoFiles.files.length === 0}
                    isLoading={downloadZipMutation.isPending}
                    className="gap-2"
                  >
                    <DownloadIcon className="h-4 w-4" />
                    Download All Photos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notes Summary - Only show if we have memoIds */}
        {hasMemoIds && memoQuery.data && (
          <div className="border-border bg-muted rounded-lg border p-4">
            <p className="text-muted-foreground text-sm">
              <span className="font-semibold">{memoQuery.data.totalCount || 0}</span> note
              {(memoQuery.data.totalCount || 0) === 1 ? '' : 's'} found
              {memoQuery.data.totalCount && memoQuery.data.totalCount > 100 && (
                <span className="text-warning-foreground ml-2">
                  (Report generation limited to 100 notes)
                </span>
              )}
            </p>
          </div>
        )}

        {/* Report type: only the types that work are offered; one type needs no picker. */}
        {availableReportTypes.length === 1 ? (
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">{availableReportTypes[0].title}</h3>
            <p className="text-muted-foreground text-sm">{availableReportTypes[0].description}</p>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-lg font-semibold">Select Report Type</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {availableReportTypes.map((type) => (
                <Card
                  key={type.id}
                  className={`cursor-pointer transition-all ${
                    reportType === type.id ? 'bg-accent ring-ring ring-2' : 'hover:bg-accent'
                  }`}
                  onClick={() => handleReportTypeClick(type.id)}
                >
                  <CardContent className="space-y-1 p-4">
                    <h4 className="font-semibold">{type.title}</h4>
                    <p className="text-muted-foreground text-sm">{type.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}

        {reportType === 'custom' && (
          <FormField
            control={form.control}
            name="customQuery"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Question</FormLabel>
                <FormControl>
                  <Textarea {...field} className="min-h-[100px]" />
                </FormControl>
              </FormItem>
            )}
          />
        )}

        {/* Form Actions - Only show when report type is selected */}
        {reportType && (
          <div className="flex items-center justify-end gap-2">
            {onClose && (
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
            )}
            <Button
              type="submit"
              disabled={
                !hasFilterApplied ||
                (reportType === 'custom' && !form.watch('customQuery')?.trim()) ||
                reportTypes.find((rt) => rt.id === reportType)?.comingSoon
              }
            >
              Generate Report
            </Button>
          </div>
        )}
      </form>

      {/* Photo Gallery Dialog */}
      <ReportPhotoGalleryDialog
        open={showPhotoGallery}
        onOpenChange={setShowPhotoGallery}
        photoFiles={photoFiles}
        fileToMemoMap={fileToMemoMap}
        categories={memoClassificationQuery.data?.results}
      />
    </Form>
  );
};
